import React, { useEffect } from 'react'
import HomeTemplate from '../templates/HomeTemplate'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import './PrivacyPolicy.css'

const PrivacyPolicy = () => {
  useCanonicalUrl();
  useEffect(() => {
    document.title = 'Privacy Policy - EveryKits';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Read the EveryKits privacy policy to learn how we handle your data. Our tools process everything locally in your browser — nothing is uploaded to our servers.');
    }
  }, []);

  return (
    <HomeTemplate>
      <div className="privacy-policy">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: March 28, 2026</p>

        <section>
          <h2>Introduction</h2>
          <p>EveryKits ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website everykits.com and use our online tools.</p>
        </section>

        <section>
          <h2>Information We Collect</h2>
          <h3>Information You Provide</h3>
          <p>When you use our tools, you may provide information such as:</p>
          <ul>
            <li>Text content for word counting and analysis</li>
            <li>Dates for age calculation</li>
            <li>Image files for compression and conversion</li>
            <li>YouTube URLs for thumbnail extraction</li>
            <li>Text for speech synthesis and QR code generation</li>
          </ul>

          <h3>Automatically Collected Information</h3>
          <p>We automatically collect certain information when you visit our website:</p>
          <ul>
            <li>IP address and browser type</li>
            <li>Device information and operating system</li>
            <li>Pages visited and time spent on our site</li>
            <li>Referring website information</li>
            <li>Click patterns and user interactions</li>
          </ul>
        </section>

        <section>
          <h2>How We Use Your Information</h2>
          <p>We use the collected information for the following purposes:</p>
          <ul>
            <li>To provide and maintain our online tools</li>
            <li>To improve user experience and website performance</li>
            <li>To analyze website traffic and usage patterns</li>
            <li>To display relevant advertisements through Google AdSense</li>
            <li>To detect and prevent fraud or abuse</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>Data Processing and Storage</h2>
          <h3>Client-Side Processing</h3>
          <p>Most of our tools process data entirely in your web browser. We do not store or transmit your personal files, text, or data to our servers unless explicitly stated.</p>

          <h3>Temporary Storage</h3>
          <p>Some data may be temporarily stored in your browser's local storage for tool functionality. This data is deleted when you clear your browser cache.</p>

          <h3>No Data Retention</h3>
          <p>We do not retain copies of your uploaded files, entered text, or generated results after your session ends.</p>
        </section>

        <section>
          <h2>Cookies and Tracking Technologies</h2>
          <p>We use cookies and similar tracking technologies:</p>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for website functionality</li>
            <li><strong>Analytics Cookies:</strong> To understand user behavior and improve our services</li>
            <li><strong>Advertising Cookies:</strong> To display relevant ads through Google AdSense</li>
            <li><strong>Preference Cookies:</strong> To remember your settings (like theme preferences)</li>
          </ul>
          <p>You can control cookies through your browser settings.</p>
        </section>

        <section>
          <h2>Third-Party Services</h2>
          <h3>Google AdSense</h3>
          <p>We use Google AdSense to display advertisements. Google may use cookies to show ads based on your visits to our site and other sites on the internet.</p>

          <h3>Google Analytics</h3>
          <p>We may use Google Analytics to analyze website traffic. Google Analytics uses cookies to collect information about how users interact with our website.</p>
        </section>

        <section>
          <h2>Data Security</h2>
          <p>We implement appropriate security measures to protect your information:</p>
          <ul>
            <li>HTTPS encryption for all data transmission</li>
            <li>Client-side processing for sensitive data</li>
            <li>Regular security updates and monitoring</li>
            <li>Limited data collection and retention</li>
          </ul>
          <p>However, no method of transmission over the internet is 100% secure.</p>
        </section>

        <section>
          <h2>Your Rights and Choices</h2>
          <p>You have the following rights regarding your data:</p>
          <ul>
            <li>Access information about our data practices</li>
            <li>Control cookies through browser settings</li>
            <li>Opt out of personalized advertising</li>
            <li>Request deletion of any personal data we have stored</li>
            <li>File a complaint with data protection authorities</li>
          </ul>
        </section>

        <section>
          <h2>Children's Privacy</h2>
          <p>Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If we become aware of such collection, we will take steps to delete it.</p>
        </section>

        <section>
          <h2>International Data Transfers</h2>
          <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers.</p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.</p>
        </section>

        <section>
          <h2>Contact Information</h2>
          <p>If you have questions about this Privacy Policy, please contact us:</p>
          <ul>
            <li>Email: privacy@everykits.com</li>
            <li>Website: everykits.com/contact</li>
          </ul>
        </section>
      </div>
    </HomeTemplate>
  )
}

export default PrivacyPolicy